'use client'

import React from 'react'
import { User } from 'lucide-react'
import { UserCircle } from 'solar-icons'
import { searchCustomer } from '@/lib/actions'
import { useCheckout, useModals, useSelectedCustomer } from '@/lib/hooks'

import {
  Avatar,
  AvatarFallback,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
} from '@/components/ui'
import { CustomerList, SearchBar } from '@/components/molecules'

export const SelectCustomer = () => {
  const { setCurrentStep } = useCheckout()
  const { openModal } = useModals()
  const { setAsGuestCustomer, setCustomer, customer, setContinueAsGuest } =
    useSelectedCustomer()

  const [input, setInput] = React.useState('')

  React.useEffect(() => {
    setInput(customer?.phone ?? '')
  }, [customer])

  const [showSearchDropdown, setShowSearchDropdown] = React.useState(false)
  const [customers, setCustomers] = React.useState<Customer[]>([])

  const handleSearchSubmit = React.useCallback(
    async (_data: FormData) => {
      const customers = await searchCustomer(input)
      setCustomers(customers)
      setShowSearchDropdown(true)
    },
    [input],
  )

  const handleSelectionChange = React.useCallback(
    async (id: any) => {
      const selectedCustomer = customers.find((c) => c.id === id?.currentKey)

      if (!selectedCustomer) {
        console.error(`No customer found with id: ${id?.currentKey}`)
        await setAsGuestCustomer()
        return
      }

      await setCustomer(selectedCustomer)
      setInput(selectedCustomer?.phone ?? '')
      setCurrentStep('SELECT_PAYMENT_METHOD')
      setShowSearchDropdown(false)
    },
    [customers, setCustomer, setCurrentStep, setAsGuestCustomer],
  )

  const handleInputChange = React.useCallback((value: string) => {
    setInput(value)
  }, [])

  return (
    <div className="flex h-full w-full flex-col items-center gap-10">
      <div className="flex w-full flex-grow flex-col items-center gap-7">
        <Avatar className="relative size-[100px] overflow-visible bg-white p-1 shadow-md">
          <AvatarFallback className="border border-gray-100 bg-gray-50 text-2xl text-gray-400 dark:border-gray-700">
            <User className="size-10" strokeWidth={1.5} />
          </AvatarFallback>
        </Avatar>

        <div className="flex w-full flex-col">
          <form
            className="flex w-full flex-col items-center gap-6"
            action={handleSearchSubmit}
          >
            <h2 className="text-lg font-medium">Search Customer</h2>
            <Popover open={showSearchDropdown}>
              <SearchBar
                autoFocus
                placeholder="Enter phone number or name"
                className="w-full bg-white pl-2"
                value={input ?? customer?.phone}
                onChangeValue={handleInputChange}
                type="text"
                isCustomerSearch={true}
              />
              <PopoverTrigger className="w-full" />
              <PopoverContent
                className="w-[280px] rounded-lg shadow-block md:w-[400px] lg:w-[500px]"
                sideOffset={-16}
              >
                <ScrollArea className="flex flex-col">
                  <CustomerList
                    customers={customers}
                    onSelectionChange={handleSelectionChange}
                  />
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </form>

          {customer && (
            <Button
              variant="brand"
              className="w-full gap-2"
              size="lg"
              onClick={() => {
                setCurrentStep('SELECT_PAYMENT_METHOD')
              }}
            >
              Continue as {customer?.name}
            </Button>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col gap-2 lg:flex-row lg:items-center">
        <Button
          variant="outline"
          className="w-full gap-2"
          size="lg"
          onClick={() => {
            setContinueAsGuest(false)
            openModal('CREATE_CUSTOMER')
          }}
        >
          <UserCircle strokeWidth={1.2} className="size-5" />
          Create New Customer
        </Button>
        <Button
          variant="default"
          className="w-full gap-2"
          size="lg"
          onClick={() => {
            setContinueAsGuest(true)
            setCurrentStep('SELECT_PAYMENT_METHOD')
          }}
        >
          <UserCircle strokeWidth={1.2} className="size-5" />
          Continue as Guest
        </Button>
      </div>
    </div>
  )
}
